import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { X } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";
import {
  applyServiceWorkerUpdate,
  getServiceWorkerUpdateAvailable,
  subscribeToServiceWorkerUpdate,
} from "../lib/pwa";

export const PWA_INSTALL_DISMISSED_KEY = "hrokbot.pwa.install-dismissed";

type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

declare global {
  interface WindowEventMap {
    beforeinstallprompt: Event;
    appinstalled: Event;
  }

  interface Navigator {
    /** iOS Safari only; `display-mode: standalone` covers other browsers. */
    standalone?: boolean;
  }
}

function isInstallPromptEvent(event: Event): event is InstallPromptEvent {
  if (!("prompt" in event) || typeof event.prompt !== "function") return false;
  return "userChoice" in event && Boolean(event.userChoice);
}

type NavigatorIdentity = Pick<Navigator, "userAgent" | "platform" | "maxTouchPoints">;

export function isIosDevice(navigatorIdentity: NavigatorIdentity | undefined): boolean {
  if (!navigatorIdentity) return false;
  if (/iPhone|iPad|iPod/i.test(navigatorIdentity.userAgent)) return true;
  // iPadOS reports a desktop Safari user agent.
  return navigatorIdentity.platform === "MacIntel" && navigatorIdentity.maxTouchPoints > 1;
}

type DisplayModeQuery = (query: string) => Pick<MediaQueryList, "matches"> | undefined;

function displayModeQuery(query: string): Pick<MediaQueryList, "matches"> | undefined {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
  try {
    return window.matchMedia(query);
  } catch {
    return undefined;
  }
}

function browserStorage(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function isStandaloneApp(
  matchMedia: DisplayModeQuery = displayModeQuery,
  navigatorIdentity: Pick<Navigator, "standalone"> | undefined = typeof navigator === "undefined"
    ? undefined
    : navigator,
): boolean {
  if (navigatorIdentity?.standalone) return true;
  return Boolean(matchMedia("(display-mode: standalone)")?.matches);
}

export function readInstallPromptDismissed(
  storage: Pick<Storage, "getItem"> | undefined = browserStorage(),
): boolean {
  try {
    return storage?.getItem(PWA_INSTALL_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberInstallPromptDismissed(
  storage: Pick<Storage, "setItem"> | undefined = browserStorage(),
): void {
  try {
    storage?.setItem(PWA_INSTALL_DISMISSED_KEY, "1");
  } catch {
    // Private mode: the prompt comes back next session instead of breaking the app.
  }
}

/**
 * The install offer and the app-update notice. It renders nothing on a desktop
 * browser that cannot install and has no waiting worker, so it adds no chrome
 * to the normal web experience.
 */
export function PwaInstall() {
  const { t } = useLingui();
  const updateAvailable = useSyncExternalStore(
    subscribeToServiceWorkerUpdate,
    getServiceWorkerUpdateAvailable,
    () => false,
  );
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null);
  const [installDismissed, setInstallDismissed] = useState(readInstallPromptDismissed);
  const [installed, setInstalled] = useState(isStandaloneApp);
  const [installing, setInstalling] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      if (!isInstallPromptEvent(event)) return;
      // Keep the browser's mini-infobar from appearing next to our own control.
      event.preventDefault();
      setPrompt(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const ios = isIosDevice(typeof navigator === "undefined" ? undefined : navigator);
  const showUpdate = updateAvailable && !updateDismissed;
  const showInstall = !showUpdate && !installDismissed && !installed && (prompt !== null || ios);
  if (!showUpdate && !showInstall) return null;

  function dismiss() {
    if (showUpdate) {
      setUpdateDismissed(true);
      return;
    }
    setInstallDismissed(true);
    rememberInstallPromptDismissed();
  }

  async function install() {
    if (!prompt || installing) return;
    setInstalling(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      setPrompt(null);
      if (choice.outcome === "accepted") setInstalled(true);
    } catch {
      // The browser withdrew the prompt; hiding it is better than a dead button.
      setPrompt(null);
    } finally {
      setInstalling(false);
    }
  }

  function reload() {
    if (reloading) return;
    setReloading(true);
    if (!applyServiceWorkerUpdate()) {
      setReloading(false);
      window.location.reload();
    }
  }

  return (
    <aside
      aria-label={showUpdate ? t`App update` : t`Install HrokBot`}
      aria-live="polite"
      data-testid="pwa-prompt"
      className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-border bg-card p-3 text-card-foreground shadow-lg sm:inset-x-auto sm:end-4 sm:mx-0"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-medium text-foreground">
          {showUpdate ? <Trans>Update available</Trans> : <Trans>Install HrokBot</Trans>}
        </p>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {showUpdate ? (
            <Trans>Reload to use the new version.</Trans>
          ) : prompt ? (
            <Trans>Add it to your home screen for faster access.</Trans>
          ) : (
            <Trans>Tap Share, then Add to Home Screen.</Trans>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {showUpdate ? (
          <Button size="sm" className="rounded-full" disabled={reloading} onClick={reload}>
            {reloading ? <Trans>Reloading…</Trans> : <Trans>Reload</Trans>}
          </Button>
        ) : prompt ? (
          <Button
            size="sm"
            className="rounded-full"
            disabled={installing}
            onClick={() => void install()}
          >
            {installing ? <Trans>Installing…</Trans> : <Trans>Install</Trans>}
          </Button>
        ) : null}
        <Button variant="ghost" size="icon-sm" aria-label={t`Dismiss`} onClick={dismiss}>
          <X size={15} strokeWidth={1.8} />
        </Button>
      </div>
    </aside>
  );
}
